import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, CheckCircle2 } from 'lucide-react';
import GenerationProgress from './GenerationProgress';

interface DocumentAIEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: string;
  documentTitle: string;
  companyName: string;
  currentContent: string;
  onContentUpdate: (newContent: string) => Promise<void>;
}

export default function DocumentAIEditModal({
  isOpen,
  onClose,
  documentType,
  companyName,
  currentContent,
  onContentUpdate,
}: DocumentAIEditModalProps) {
  const [instruction, setInstruction] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const documentNames: Record<string, string> = {
    'PLAN_DIRECTEUR': 'Plan Directeur TI',
    'PLAN_DIRECTEUR_5_ANS_V2': 'Plan Directeur 5 ans V2',
    'ODS': 'Offre de Service',
    'ROADMAP': 'Roadmap Annuel',
    'CAHIER_CHARGE': "Spécifications d'exigences d'affaires et TI",
    'BILAN_SANTE': 'Bilan de santé TI',
    'AUDIT_SECURITE': 'Audit de sécurité',
    'PLAN_BUDGETAIRE_TI_N1': 'Plan budgétaire TI annuel (N+1)',
    'PLAN_BUDGETAIRE_5_ANS': 'Plan budgétaire TI – Vision 5 ans',
    'REGISTRE_AMELIORATION_CONTINUE_TI': 'Registre d\'amélioration continue (backlog TI)',
    'REGISTRE_RISQUES_TI': 'Registre des risques TI & plan de traitement',
    'OFFRE_SERVICE_BZ': 'Offre de service BZ - Solution d\'affaire holistique',
  };

  const documentName = documentNames[documentType] || 'Document';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!instruction.trim() || isProcessing) return;

    setIsProcessing(true);
    setGenerationProgress(0);
    setGenerationStep('Analyse de votre demande...');
    setError(null);
    setShowSuccess(false);

    // Animation de progression
    const steps = [
      { progress: 15, text: 'Analyse de votre instruction et du document actuel...', delay: 1000 },
      { progress: 35, text: 'Identification des sections à modifier...', delay: 1200 },
      { progress: 55, text: 'Application des modifications avec précision...', delay: 1500 },
      { progress: 75, text: 'Vérification de la cohérence et du style...', delay: 1400 },
      { progress: 90, text: 'Finalisation et préparation du document modifié...', delay: 1000 },
    ];

    let stepIndex = 0;
    const intervalId = window.setInterval(() => {
      if (stepIndex < steps.length) {
        const stepDef = steps[stepIndex];
        setGenerationStep(stepDef.text);
        setGenerationProgress(stepDef.progress);
        stepIndex++;
      }
    }, 1500);

    try {
      // Appeler le backend pour modifier le document (évite les problèmes CORS)
      const response = await fetch('/api/prisme/documents/edit-with-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          currentContent,
          instruction,
          documentType,
          companyName,
        }),
      });

      clearInterval(intervalId);
      setGenerationProgress(100);
      setGenerationStep('Finalisation...');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur serveur (${response.status})`);
      }

      const data = await response.json();
      
      if (!data.content) {
        throw new Error('Format de réponse invalide de l\'API');
      }

      const modifiedContent = data.content;

      // Mettre à jour le document dans la base de données
      await onContentUpdate(modifiedContent);

      // Afficher le succès
      setShowSuccess(true);
      setInstruction('');

      // Fermer après 2 secondes
      setTimeout(() => {
        setShowSuccess(false);
        setIsProcessing(false);
        onClose();
      }, 2000);

    } catch (error) {
      clearInterval(intervalId);
      console.error('Erreur lors de la modification:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setError(errorMessage);
      setIsProcessing(false);
      setGenerationProgress(0);
    }
  };

  if (!isOpen) return null;

  // Écran de progression
  if (isProcessing && !showSuccess) {
    return (
      <GenerationProgress
        currentStep={generationStep || 'Traitement en cours...'}
        progress={generationProgress}
        documentLabels={{
          doc1: 'Modification',
          doc2: 'Document',
        }}
      />
    );
  }

  // Écran de succès
  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-bz-dark via-bz-blue to-purple-900 z-50 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="flex justify-center mb-4"
          >
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" />
            </div>
          </motion.div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Modification réussie !
          </h3>
          <p className="text-gray-600 mb-4">
            Le document a été mis à jour avec succès.
          </p>
          <p className="text-sm text-gray-500">
            Retour au document...
          </p>
        </motion.div>
      </div>
    );
  }

  // Modal de saisie de l'instruction
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6" />
              <div>
                <h2 className="text-2xl font-bold">Éditer avec IA</h2>
                <p className="text-purple-100 text-sm">{documentName}</p>
                <p className="text-purple-200 text-xs mt-1">{companyName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm font-semibold mb-2">
                💡 Comment modifier votre document
              </p>
              <p className="text-blue-700 text-sm mb-2">
                Décris simplement la modification que tu veux apporter. L'IA va appliquer ta demande tout en conservant l'intégralité du reste du document.
              </p>
              <p className="text-blue-800 text-sm font-semibold mt-3 mb-2">
                Exemples de modifications :
              </p>
              <ul className="text-blue-700 text-sm space-y-1 ml-4 list-disc">
                <li>"Ajoute un 6e axe stratégique : Programmation et automatisation"</li>
                <li>"Rends le ton plus formel et professionnel"</li>
                <li>"Ajoute plus de détails sur la gouvernance TI"</li>
                <li>"Change la durée du projet à 9 mois au lieu de 6"</li>
                <li>"Ajoute une section sur la formation des utilisateurs"</li>
              </ul>
            </div>

            {/* Chat input */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quelle modification veux-tu apporter à ce document ?
                </label>
                <textarea
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="Ex: Ajoute un 6e axe stratégique nommé 'Programmation et automatisation' avec des détails sur l'automatisation des processus métier..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows={5}
                  disabled={isProcessing}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={!instruction.trim() || isProcessing}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Send className="w-5 h-5" />
                  Modifier le document avec IA
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
